/**
 * NC template form content. Receives all state and handlers via ctx from useNCTemplateCore.
 * Uses useStore() for bidDetails and templateType.
 */
import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useStore } from 'hooks/useStore';
import { ProjectInformationSection } from '../sections';
import { preserveGlobalFields as preserveGlobalFieldsLogic } from '../../bidAnalysisTemplate.logic';

export type NCFormContentProps = {
  ctx: Record<string, any>;
};

export const NCFormContent = observer((props: NCFormContentProps) => {
  const { ctx: c } = props;
  const { BidAnalysisStore } = useStore();
  const bidDetails = BidAnalysisStore.currentBidDetails;
  const templateType = bidDetails?.template_type || 'mas';

  return (
    <>
      {/* NC Form - Similar to Direct but with Year Tabs */}
      <div ref={el => (c.sectionRefs.current['project-details'] = el)}>
        <ProjectInformationSection
          isExpanded={c.expandedSections.projectInfo}
          onToggle={() => c.toggleSection('projectInfo')}
          templateData={c.templateData}
          formData={
            {
              ...c.formData,
              targetLaunchDate:
                c.activePackSizeTab != null
                  ? c.yearFormData[c.activePackSizeTab]?.[0]?.targetLaunchDate ?? c.formData.targetLaunchDate
                  : c.formData.targetLaunchDate,
            }
          }
          analysisType={c.analysisType}
          gsmsHndlgType={c.gsmsHndlgType}
          supplierName={c.supplierName}
          supplierOptions={c.supplierOptions}
          contractsList={c.contractsList}
          handlingTypesList={c.handlingTypesList}
          isIndirect={false}
          pricingValidatedChecked={c.pricingValidatedChecked}
          validationErrors={c.validationErrors}
          onFieldChange={c.handleFieldChange}
          onFieldBlur={c.handleFieldBlur}
          onAnalysisTypeChange={c.setAnalysisType}
          onGsmsHndlgTypeChange={c.handleGsmsHndlgTypeChange}
          onSupplierChange={c.setSupplierName}
          isSaving={c.isSaving}
          templateType={templateType}
          onContractsChange={(selected: any, contractsData: any) => {
            const hasContracts =
              contractsData && Array.isArray(contractsData) && contractsData.length > 0;
            const contractsString = hasContracts
              ? templateType === 'nc'
                ? contractsData.map((ct: any) => ct.value || ct.label || ct.contract_name).join(', ')
                : contractsData.map((ct: any) => ct.contract_name).join(', ')
              : '';
            c.setYearFormData((prev: any) => {
              const updated: any = {};
              Object.keys(prev).forEach(yearIndex => {
                const yearData = prev[parseInt(yearIndex)];
                if (yearData) {
                  const { contracts: _c2, contractsData: _cd2, ...rest } = yearData;
                  updated[parseInt(yearIndex)] = rest;
                }
              });
              return updated;
            });
            c.setFormData((prev: any) => ({
              ...prev,
              contracts: contractsString,
              contractsData: hasContracts ? contractsData : [],
            }));
          }}
          notesCount={c.notes.filter((n: any) => n.section_name === 'manager_modification').length}
          onNotesClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            c.handleNotesClick('manager_modification', e)
          }
        />
      </div>

      {/* Product Details Section with Year Tabs */}
      <div
        ref={el => (c.sectionRefs.current['product-info'] = el)}
        className={c.classes?.section ?? ''}
      >
        <div
          className={c.classes?.sectionHeader ?? ''}
          onClick={() => c.toggleSection('productDetails')}
        >
          <h2>Product Details</h2>
          <Button
            icon="pi pi-comment"
            className={c.classes?.notesButton ?? ''}
            onClick={e => {
              e.stopPropagation();
              c.handleNotesClick(c.SECTION_NAMES.PRODUCT_DETAILS, e);
            }}
            label={`Notes (${c.notes.filter((n: any) => n.section_name === c.SECTION_NAMES.PRODUCT_DETAILS).length})`}
            text
            size="small"
          />
          <i
            className={`pi ${c.expandedSections.productDetails ? 'pi-chevron-up' : 'pi-chevron-down'}`}
          />
        </div>

        {c.expandedSections.productDetails && (
          <div className={c.classes?.sectionContent ?? ''}>
            <div
              className={c.classes?.formField ?? ''}
              style={{ maxWidth: '400px', marginBottom: '1.5rem' }}
            >
              <label>
                Description <span className={c.classes?.required ?? ''}>*</span>
              </label>
              <Dropdown
                value={c.selectedConcept}
                options={c.concepts}
                onChange={e => {
                  const concept = e.value;
                  c.setSelectedConcept(concept);
                  if (bidDetails?.form_details?.form_data) {
                    const conceptIndex = bidDetails.form_details.form_data.findIndex(
                      (fc: any) => fc.concept_name === concept
                    );
                    const conceptData = bidDetails.form_details.form_data[conceptIndex];
                    if (conceptData) {
                      const packSizes = Object.keys(conceptData.pack_size || {});
                      if (packSizes.length > 0) {
                        const tabIndex = conceptIndex * 1000 + 0;
                        if (c.packSizeFormData[tabIndex]) {
                          c.setActivePackSizeTab(tabIndex);
                          const tabData = c.packSizeFormData[tabIndex];
                          c.setFormData(
                            preserveGlobalFieldsLogic({ ...tabData, conceptName: concept }, c.formData)
                          );
                        } else {
                          const conceptTabs = Object.keys(c.packSizeFormData)
                            .map(key => parseInt(key))
                            .filter(index => Math.floor(index / 1000) === conceptIndex);
                          if (conceptTabs.length > 0) {
                            const firstTab = conceptTabs[0];
                            c.setActivePackSizeTab(firstTab);
                            const tabData = c.packSizeFormData[firstTab];
                            c.setFormData(
                              preserveGlobalFieldsLogic({ ...tabData, conceptName: concept }, c.formData)
                            );
                          }
                        }
                      }
                    }
                  }
                }}
                placeholder="Select Concept"
                filter
                style={{ width: '100%' }}
              />
            </div>

            {c.selectedConcept && c.packSizeTabs.length > 0 ? (
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.5rem',
                    display: 'block',
                  }}
                >
                  Pack Sizes
                </label>
                <div className={c.classes?.ndcTabs ?? ''}>
                  {c.packSizeTabs.map((tab: any) => (
                    <div
                      key={tab.index}
                      className={`${c.classes?.ndcTab ?? ''} ${c.activePackSizeTab === tab.index ? (c.classes?.active ?? '') : ''}`}
                      onClick={() => c.handlePackSizeTabChange(tab.index)}
                    >
                      Pack Size {tab.packSize}
                    </div>
                  ))}
                </div>
              </div>
            ) : c.selectedConcept ? (
              <div
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  background: '#fef3c7',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                }}
              >
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ fontSize: '2rem', color: '#f59e0b', marginBottom: '0.5rem' }}
                />
                <h3 style={{ color: '#92400e', marginBottom: '0.5rem' }}>No Pack Sizes Available</h3>
                <p style={{ color: '#78350f' }}>
                  No pack sizes are available for the selected concept.
                </p>
              </div>
            ) : (
              <div
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                }}
              >
                <p style={{ color: '#6b7280' }}>Please select a concept to view pack sizes.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
});
